import Popper from "@mui/material/Popper";
import { cloneElement, useEffect, useRef } from "react";

const boundedScreenSelector =
  ".rcti-editor-shell, .rcti-flow-shell, .rcti-agreements__content, .rcti-payment-backdrop, .debit-page, .debit-modal, .credit-editor, .credit-detail-page, .credit-note-page";

export const createMenuSizing = (boundaryEl) => ({
  name: "catalogueModalMenuSizing",
  enabled: true,
  phase: "beforeWrite",
  requires: ["computeStyles"],
  fn({ state }) {
    const rect = state.elements.reference.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop || 0;
    const viewportHeight =
      viewport?.height ||
      document.documentElement.clientHeight ||
      window.innerHeight;
    const viewportWidth =
      viewport?.width ||
      document.documentElement.clientWidth ||
      window.innerWidth;
    const boundaryRect = boundaryEl?.getBoundingClientRect();
    const top = Math.max(viewportTop, boundaryRect?.top ?? viewportTop);
    const bottom = Math.min(
      viewportTop + viewportHeight,
      boundaryRect?.bottom ?? viewportTop + viewportHeight
    );
    const available = state.placement.startsWith("top")
      ? Math.min(rect.top, bottom) - top
      : bottom - Math.max(rect.bottom, top);
    state.styles.popper.width = `${Math.max(
      0,
      Math.min(rect.width, viewportWidth - 16)
    )}px`;
    state.elements.popper.style.setProperty(
      "--catalogue-modal-menu-height",
      `${Math.max(0, Math.min(224, available - 14))}px`
    );
  },
});

export default function CatalogueModalDropdownMenu({
  anchorEl,
  boundaryEl,
  children,
  containerEl,
  disableFlip = false,
}) {
  const popperRef = useRef(null);
  const modal = anchorEl?.closest(".quotation-catalogue-modal");
  const container =
    containerEl ||
    modal?.closest('[data-catalogue-dropdown-portals="true"]') ||
    anchorEl?.closest(boundedScreenSelector);
  useEffect(() => {
    if (!container) return undefined;
    const update = () => popperRef.current?.update();
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (anchorEl) observer?.observe(anchorEl);
    const menu = popperRef.current?.state?.elements?.popper;
    if (menu) observer?.observe(menu);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    return () => {
      observer?.disconnect();
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, [anchorEl, container]);
  if (!container) return children;

  return (
    <Popper
      open
      anchorEl={anchorEl}
      popperRef={popperRef}
      container={() => container}
      placement="bottom-start"
      popperOptions={{ strategy: "fixed" }}
      modifiers={[
        { name: "offset", options: { offset: [0, 6] } },
        {
          name: "flip",
          enabled: !disableFlip,
          options: { rootBoundary: "viewport", padding: 8 },
        },
        {
          name: "preventOverflow",
          options: {
            rootBoundary: "viewport",
            padding: 8,
            altAxis: true,
            tether: false,
          },
        },
        createMenuSizing(boundaryEl),
      ]}
      className="catalogue-modal-menu-popper"
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        "& .quotation-figma-dropdown-menu": {
          boxSizing: "border-box",
          position: "relative",
          inset: "auto",
          width: "100%",
          minWidth: 0,
          maxHeight: "var(--catalogue-modal-menu-height, 224px)",
          overflowX: "hidden",
          overflowY: "auto",
          overscrollBehavior: "contain",
          scrollbarGutter: "stable",
        },
      }}
    >
      <div
        className={anchorEl?.className}
        style={{ position: "static", width: "100%" }}
      >
        {cloneElement(children, {
          style: {
            ...children.props.style,
            boxSizing: "border-box",
            position: "relative",
            inset: "auto",
            width: "100%",
            minWidth: 0,
            maxHeight: "var(--catalogue-modal-menu-height, 224px)",
            overflowX: "hidden",
            overflowY: "auto",
            overscrollBehavior: "contain",
            scrollbarGutter: "stable",
          },
        })}
      </div>
    </Popper>
  );
}
